import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@drpcrd/utils/shared";
import { forwardRef, HTMLAttributes } from "react";

const alertVariants = cva(
  "relative w-full rounded-2xl border p-2 [&>svg~*]:pl-5 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-2 [&>svg]:top-2 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        info: "bg-primary/10 border-primary/50 text-primary [&>svg]:text-primary",
        warning:
          "bg-yellow-500/10 border-yellow-500/50 text-yellow-500 [&>svg]:text-yellow-500",
        danger:
          "bg-red-500/10 border-red-500/50 text-red-500 [&>svg]:text-red-500",
      },
    },
    defaultVariants: {
      variant: "info",
    },
  },
);

/**
 * Alert component for displaying important messages to users with different
 * severity levels. Built using compound components for maximum flexibility.
 *
 * @default
 * `variant="info"`
 *
 * @example
 * ```tsx
 * import { Alert, AlertTitle, AlertDescription } from '@drpcrd/shared/ui';
 * import { Info } from 'lucide-react';
 *
 * <Alert variant="info">
 *   <Info />
 *   <AlertTitle>Heads up!</AlertTitle>
 *   <AlertDescription>
 *     You can add components to your app using the cli.
 *   </AlertDescription>
 * </Alert>
 * ```
 */
export const Alert = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = "Alert";

/**
 * AlertTitle component for displaying the alert heading.
 *
 * @example
 * ```tsx
 * <AlertTitle>Update available</AlertTitle>
 * ```
 */
export const AlertTitle = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

/**
 * AlertDescription component for displaying the detailed alert message.
 *
 * @example
 * ```tsx
 * <AlertDescription>
 *   You can add components to your app using the cli.
 * </AlertDescription>
 * ```
 */
export const AlertDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";
